import React, { useRef, useState } from "react";

import { ITagSets } from "@formant/core/model/ITagSets";
import {
    ChevronDown,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    Minus,
    Popover,
    PopoverContent,
    PopoverTrigger,
    X,
} from "@formant/ui-kit";

interface IChooseTagsProps {
    tags: ITagSets;
    currentTags?: ITagSets;
    onTagsChange: (selectedTags: ITagSets) => void;
}

export const ChooseTags: React.FC<IChooseTagsProps> = ({
    tags,
    currentTags,
    onTagsChange,
}) => {
    const [selectedTags, setSelectedTags] = useState<ITagSets>(
        currentTags || {},
    );
    const [openCategory, setOpenCategory] = useState<string | null>(null);
    const clearingTag = useRef<string | null>(null);

    const handleTagSelect = (category: string, value: string) => {
        const newTags = { ...selectedTags };

        if (!newTags[category]) {
            newTags[category] = [];
        }

        const valueIndex = newTags[category].indexOf(value);
        if (valueIndex === -1) {
            // Add value if not present
            newTags[category] = [...newTags[category], value];
        } else {
            // Remove value if already selected
            newTags[category] = newTags[category].filter(v => v !== value);
            // Remove category if no values left
            if (newTags[category].length === 0) {
                delete newTags[category];
            }
        }

        setSelectedTags(newTags);
        onTagsChange(newTags);
    };

    const removeTag = (
        category: string,
        value: string | null,
        e: React.MouseEvent,
    ) => {
        e.preventDefault();
        e.stopPropagation();

        clearingTag.current = category;

        const newTags = { ...selectedTags };

        if (value === null) {
            // Remove entire category
            delete newTags[category];
        } else {
            // Remove specific value
            newTags[category] = newTags[category].filter(v => v !== value);
            if (newTags[category].length === 0) {
                delete newTags[category];
            }
        }

        setSelectedTags(newTags);
        onTagsChange(newTags);

        setTimeout(() => {
            clearingTag.current = null;
        }, 100);
    };

    const isValueSelected = (category: string, value: string) => {
        return selectedTags[category]?.includes(value) || false;
    };

    return (
        <div className="w-full">
            <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(tags).map(([category]) => {
                    const isSelected = selectedTags[category]?.length > 0;
                    const selectedValues = selectedTags[category] || [];

                    return (
                        <Popover
                            key={category}
                            open={openCategory === category}
                            onOpenChange={open => {
                                if (clearingTag.current === category) {
                                    return;
                                }

                                if (open) {
                                    setOpenCategory(category);
                                } else if (openCategory === category) {
                                    setOpenCategory(null);
                                }
                            }}
                        >
                            <PopoverTrigger asChild={true}>
                                <span
                                    className={` ${isSelected ? "!border-formant-color-border-active" : ""} border-b !hover:bg-transparent h-8 !px-0 py-1 !rounded-none cursor-pointer border-formant-color-border-secondary focus:border-formant-color-border-active pb-2.5 pt-2.5 text-formant-color-text-primary  flex items-center gap-1 !hover:border-formant-color-border-active `}
                                >
                                    {isSelected ? (
                                        <div className="flex items-center flex-wrap gap-1">
                                            <span>{category}:</span>
                                            {selectedValues.map(value => (
                                                <div
                                                    key={value}
                                                    className="flex items-center bg-formant-color-background-secondary px-2 py-0.5 rounded-full"
                                                >
                                                    <span className="text-sm text-formant-color-text-primary">
                                                        {value}
                                                    </span>
                                                    <div
                                                        onClick={e =>
                                                            removeTag(
                                                                category,
                                                                value,
                                                                e,
                                                            )
                                                        }
                                                        className="ml-1 p-0.5 rounded-full hover:bg-formant-color-background-active cursor-pointer"
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </div>
                                                </div>
                                            ))}
                                            <div
                                                onClick={e =>
                                                    removeTag(category, null, e)
                                                }
                                                className="ml-1 p-1 rounded-full hover:bg-formant-color-background-secondary cursor-pointer"
                                            >
                                                <X className="h-3 w-3" />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {category}
                                            <ChevronDown className="h-4 w-4 ml-1" />
                                        </>
                                    )}
                                </span>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                className="w-[200px] relative z-50 max-h-96 min-w-[8rem] overflow-hidden bg-formant-color-background-surface border-formant-color-border-active rounded-md border  text-formant-color-text-secondary shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                            >
                                <Command className="bg-transparent">
                                    <CommandInput
                                        placeholder="Search..."
                                        className="border-b mb-4 rounded-none border-formant-color-border-secondary text-white"
                                    />
                                    <CommandList className="max-h-[200px] overflow-auto">
                                        <CommandEmpty>
                                            No results found.
                                        </CommandEmpty>
                                        {tags[category]?.map(value => (
                                            <CommandItem
                                                key={value}
                                                onSelect={() =>
                                                    handleTagSelect(
                                                        category,
                                                        value,
                                                    )
                                                }
                                                className="cursor-pointer hover:bg-formant-color-background-secondary flex items-center gap-2 py-2"
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded ${isValueSelected(category, value) ? "bg-formant-color-background-secondary border-formant-color-background-secondary" : "border-formant-color-border-secondary"}`}
                                                >
                                                    {isValueSelected(
                                                        category,
                                                        value,
                                                    ) && (
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            className="w-4 h-4 text-white"
                                                        >
                                                            <path
                                                                d="M20 6L9 17L4 12"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                                {value}
                                            </CommandItem>
                                        ))}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    );
                })}
            </div>
        </div>
    );
};
